












































































import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  ref,
  ssrRef,
  useContext,
  useFetch,
  watch
} from '@nuxtjs/composition-api';
import { SfButton, SfLink } from '@storefront-ui/vue';
import { useElementSize } from '@vueuse/core';

import contentfulEntriesIds from '~/diptyqueTheme/config/contentfulEntries';
import type { CountDownDataInterface } from '~/diptyqueTheme/types/components';
import { useContentfulGQL } from '~/integrations/contentful/src/composables/useContentfulGQL';

const PROMO_COLORS = {
  GREY: '#f8f8f8',
  BLACK: '#000000',
  WHITE: '#ffffff'
};

const PROMO_TIME = {
  // eslint-disable-next-line no-magic-numbers
  ONE_WEEK: 7 * 24 * 60 * 60
};

export default defineComponent({
  name: 'VaimoPromoBanner',
  components: {
    CountdownTimer: () => import('atoms/CountdownTimer.vue'),
    SfButton,
    SfLink
  },
  emits: ['promo-closed'],
  setup(_, { emit }) {
    const { query, queryResponse } = useContentfulGQL('byKeyPromoBanner');
    const promoBannerHeight = ref(0);
    const promoBannerText = ref('');
    const promoBannerLink = ref('');
    const promoBannerRef = ref(null);
    const promoBannerContentRef = ref(null);
    const countDownData = ref<CountDownDataInterface>({
      countdownEndDataTime: '',
      countdownShowHideSeconds: false,
      countdownShowHide: false,
      countDownExpiredFallBackText: ''
    });
    const isCountdownExpired = ssrRef(false);
    const promoBackgroundColor = ref(PROMO_COLORS.GREY);
    const promoTextColor = ref(PROMO_COLORS.BLACK);
    const { i18n, app } = useContext();
    const apiState = app.$vsf.$magento.config.state;

    const isBannerHidden = ref(true);

    // Helper to sync state from cookie/localStorage
    const syncBannerHidden = () => {
      isBannerHidden.value = !!apiState.getPromoBannerClosed();
    };

    onMounted(() => {
      if (process.client) {
        syncBannerHidden();
      }

      nextTick(() => {
        const size = useElementSize(promoBannerRef, {
          width: 0,
          height: 0
        });

        promoBannerHeight.value = size.height.value;

        watch(
          () => size.height.value,
          (newHeight) => {
            promoBannerHeight.value = newHeight;
          }
        );
      });
    });

    const isCountDownEnabled = computed<boolean>(() => {
      const countdownEndDataTime =
        countDownData.value?.countdownEndDataTime || '';
      const countdownShowHide = countDownData.value?.countdownShowHide || false;

      return (
        Boolean(countdownEndDataTime) &&
        countdownShowHide &&
        !isCountdownExpired.value
      );
    });

    useFetch(async () => {
      await query('getMicroDataBunchByKey', {
        key: contentfulEntriesIds.PROMO_BANNER_MICRODATA_ITEM_KEY,
        locale: i18n.localeProperties.contentfulLocale
      });

      const promoBannerContent =
        queryResponse.value?.microdataBunchCollection?.items?.[0]
          ?.entriesCollection?.items || [];

      if (promoBannerContent && promoBannerContent.length) {
        promoBannerContent.forEach(
          ({
            key,
            value,
            longValue,
            countdownEndDataTime,
            countdownShowHideSeconds,
            countdownShowHide,
            promoBannerBackgroundColor,
            promoBannerTextColor
          }) => {
            if (key === 'promo-banner-text') {
              let text = value;
              if (text === '##') text = ''; // Make '##' empty string to make it possible to hide banner for specific locales
              promoBannerText.value = text;

              if (countdownEndDataTime) {
                countDownData.value = {
                  countdownEndDataTime,
                  countdownShowHideSeconds,
                  countdownShowHide,
                  countDownExpiredFallBackText: longValue
                };
              }
              // To avoid CLS during calculation in CountdownTimer component
              isCountdownExpired.value = countdownEndDataTime
                ? new Date(countdownEndDataTime).getTime() -
                    new Date().getTime() <=
                  0
                : true;

              promoBackgroundColor.value =
                promoBannerBackgroundColor || PROMO_COLORS.GREY;

              promoTextColor.value = promoBannerTextColor
                ? PROMO_COLORS.BLACK
                : PROMO_COLORS.WHITE;
            } else if (key === 'promo-banner-link') {
              promoBannerLink.value = value;
            }
          }
        );
      }
    });

    const setCountdownExpiredFlag = (flag: boolean) => {
      isCountdownExpired.value = flag;
      promoBannerText.value =
        countDownData.value?.countDownExpiredFallBackText || '';
    };

    const closePromoBanner = () => {
      apiState.setPromoBannerClosed(true, {
        maxAge: PROMO_TIME.ONE_WEEK
      });

      isBannerHidden.value = true;
      emit('promo-closed');
    };

    return {
      promoBannerRef,
      countDownData,
      isCountDownEnabled,
      promoBannerContentRef,
      isBannerHidden,
      promoBannerHeight,
      promoBannerText,
      promoBannerLink,
      setCountdownExpiredFlag,
      closePromoBanner,
      promoBackgroundColor,
      promoTextColor
    };
  }
});
